'use client';

import React, {
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Link from 'next/link';
import classNames from 'classnames';

// Types & Styles
import {
  EClickableSize,
  EClickableTarget,
} from '@/components/atoms/at-clickable/at-clickable.types';
import './at-nav-link.css';
import '../at-clickable/at-clickable.css';
import atNavLink from './at-nav-link.variants';

// Contexts & Utils
import { useLocation } from '@/context/locationContext';
import { getRegionUrlById } from '@/components/templates/tm-home-plan-detail/utils/locationMapper';
import { LoginModalContext } from '@/context/loginModalContext';
import { useLeadGenModal } from '@/context/leadGenModalContext';
import { openChat } from '@/components/integrations/GenesysLoader';
import {
  sendGTMClickEvent,
  sendGTMNavigationEvent,
} from '@/components/helpers/analytics/gtm';
import { getDesignStudioCalendlyLink } from '@/lib/contentstack-sdk';
import {
  ESetLocationType,
  useSetLocationModal,
} from '@/context/setLocationContext';
import { IAtNavLinkProps } from './at-nav-link.types';
import { useInitialProps } from '@/app/layout.client';

/**
 * `AtNavLink` is a dynamic navigation component that conditionally renders as a `<button>` or `<a>`.
 * It handles internal app behaviors like chat, login modals, and Calendly scheduling,
 * as well as standard navigation and GTM tracking.
 *
 * - Renders a button for special internal logic (e.g., :openChat, :openLoginModal)
 * - Renders an anchor link for normal navigation (internal or external)
 * - Automatically resolves redirect URLs for login and studio flows
 * - Tracks user interactions via GTM analytics
 *
 */
const AtNavLink = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  IAtNavLinkProps
>(
  (
    {
      variant,
      label = '',
      size = EClickableSize.LG,
      children,
      target = EClickableTarget.SELF,
      href,
      className,
      onClick,
      dataTestId = 'nav-link',
      selected = false,
      onMouseEnter,
      onMouseLeave,
      interactLocation = 'at-nav-link',
      ariaLabel,
      shouldSendGTMEvent = true,
      ...rest
    },
    ref,
  ) => {
    const { studio3DData } = useInitialProps();
    const { currentBuildStudio } = useLocation();
    const { buildLoginUrl } = useContext(LoginModalContext);
    const { openLeadGenModal } = useLeadGenModal();
    const { handleOpen } = useSetLocationModal();

    const [calendlyLink, setCalendlyLink] = useState<string | undefined>();
    const classes = atNavLink({ size, type: variant, selected });

    // Extract URL params once for login flow
    const hrefParams = useMemo(() => {
      if (href?.includes('?')) {
        return new URLSearchParams(href.split('?')[1]);
      }
      return new URLSearchParams();
    }, [href]);

    const loginAction = hrefParams.get('LoginAction');

    const currentHref = useMemo(() => {
      if (href?.startsWith(':includedFeatures')) {
        return getRegionUrlById(currentBuildStudio?.Id);
      }

      if (href?.startsWith(':openLoginModal')) {
        return buildLoginUrl({
          StudioId:
            currentBuildStudio?.Id?.toString() ||
            hrefParams.get('StudioId') ||
            undefined,
          PlanId: hrefParams.get('PlanId') || undefined,
          ElevationId: hrefParams.get('ElevationId') || undefined,
          LoginAction: loginAction || undefined,
        });
      }

      return href ?? '';
    }, [href, currentBuildStudio?.Id, buildLoginUrl, hrefParams, loginAction]);

    useEffect(() => {
      const fetchCalendlyUrl = async () => {
        if (!currentBuildStudio?.Id) return;
        const link = await getDesignStudioCalendlyLink(
          currentBuildStudio.Id,
          studio3DData,
        );
        setCalendlyLink(link);
      };

      fetchCalendlyUrl();
    }, [currentBuildStudio?.Id, studio3DData]);

    // Behavior flags
    const isOpenChat = href === ':openChat';
    const isLoginFlow = href?.startsWith(':openLoginModal');
    const isOpenLeadGen =
      href?.startsWith(':openLeadGen') ||
      (href?.startsWith(':calendly') && !calendlyLink);

    const shouldRenderAsButton =
      !href ||
      isOpenChat ||
      isOpenLeadGen ||
      (isLoginFlow && !currentBuildStudio);

    const handleClick = () => {
      if (shouldSendGTMEvent) {
        sendGTMClickEvent(label, interactLocation, href || 'not_available');
      }

      if (isOpenChat) return openChat(interactLocation);
      if (isOpenLeadGen && href) return openLeadGenModal(href);
      if (isLoginFlow && !currentBuildStudio) {
        const action = hrefParams?.get('LoginAction');
        return handleOpen({
          type:
            action == 'LOT_VISION'
              ? ESetLocationType.LOT_VISION
              : ESetLocationType.LOGIN,
          redirect: currentHref,
        });
      }

      onClick?.();
    };

    if (shouldRenderAsButton) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          className={classNames(className, classes)}
          onClick={handleClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          data-testid={dataTestId}
          aria-label={ariaLabel}
          data-selected={selected || undefined}
          {...rest}
        >
          <span className="flex items-center">
            {label}
            {children}
          </span>
        </button>
      );
    }

    return (
      <Link
        href={currentHref}
        passHref
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={classNames(className, classes)}
        target={target}
        onClick={() => {
          if (shouldSendGTMEvent) {
            sendGTMNavigationEvent(label, interactLocation, href);
          }
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-testid={dataTestId}
        aria-label={ariaLabel}
      >
        <span className="flex items-center">
          {label}
          {children}
        </span>
      </Link>
    );
  },
);

AtNavLink.displayName = 'AtNavLink';

export { AtNavLink };
